import { useEffect, useRef, useState } from 'react'
import { Building2, CircleMinus } from '@/components/icons'
import { PrntrPickerRow } from './ui/prntr-input'
import { Skeleton } from './ui/skeleton'
import { SwipeableRow } from './ui/swipeable-row'
import { PhoneLink, EmailLink } from './ContactLink'
import { useFbosByAirport } from '@/lib/hooks'
import { FboPickerDrawer } from './FboPickerDrawer'
import { track, EVENTS } from '@/utils/analytics'

// Shape of an FBO row as served by fbo.listByAirport / included on trip legs.
export interface FboRecord {
  id: string
  name: string
  phone: string | null
  email: string | null
  vhfFrequency: string | null
  website: string | null
  source: string
}

interface FboSectionProps {
  /** Watched form ICAO; empty/undefined renders nothing */
  icao?: string
  /** Currently selected FBO id (form state) */
  value: string | null
  onChange: (fboId: string | null) => void
  kind: 'departure' | 'arrival'
  readOnly: boolean
  /** Saved FBO relation from the leg — fallback display when the lookup fails */
  savedFbo?: FboRecord | null
  /**
   * Permits auto-selecting a lone result. Kept off for a pristine edit-mode
   * drawer so a saved "no FBO" choice isn't overwritten and the form isn't
   * spuriously dirtied; the parent enables it once the user changes the airport.
   */
  allowAutoSelect: boolean
}

// Compact FBO / handling-agent block rendered under each airport field in the
// flight form. Fetches FBOs on airport selection and lets the user attach one
// to this end of the leg.
export function FboSection({
  icao,
  value,
  onChange,
  kind,
  readOnly,
  savedFbo,
  allowAutoSelect,
}: FboSectionProps) {
  const [isPickerOpen, setIsPickerOpen] = useState(false)
  // Swipe-to-remove state for the selected-FBO row
  const [isRowOpen, setIsRowOpen] = useState(false)

  const { data, isLoading, isError } = useFbosByAirport(icao)
  const fbos = (data ?? undefined) as FboRecord[] | undefined

  // fbo_fetched once per airport per mount
  const trackedIcaosRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (!icao || !fbos || trackedIcaosRef.current.has(icao)) return
    trackedIcaosRef.current.add(icao)
    track(EVENTS.FBO_FETCHED, {
      icao,
      kind,
      count: fbos.length,
      sources: [...new Set(fbos.map((f) => f.source))],
    })
  }, [icao, fbos, kind])

  // Auto-select a lone result. Guarded so it never clobbers a saved selection:
  // only when the parent allows it, the field is empty, and exactly one FBO exists.
  useEffect(() => {
    if (!allowAutoSelect || readOnly || value != null || fbos?.length !== 1) return
    onChange(fbos[0].id)
    track(EVENTS.FBO_SELECTED, { icao, kind, source: fbos[0].source, auto: true })
  }, [allowAutoSelect, readOnly, value, fbos, onChange, icao, kind])

  if (!icao) return null

  const selected: FboRecord | null =
    (value && fbos?.find((f) => f.id === value)) ||
    (value && savedFbo?.id === value ? savedFbo : null) ||
    null

  // Loading with nothing to show yet → one skeleton row. If the saved FBO
  // already covers the selection, fall through and render it immediately.
  if (isLoading && !selected) {
    return <Skeleton className="my-2 h-8 w-full rounded-lg" />
  }

  // Fetch failed and nothing selected → stay out of the way.
  if (isError && !selected) return null

  const openPicker = () => {
    if (!readOnly) setIsPickerOpen(true)
  }

  const drawers = (
    <FboPickerDrawer
      isOpen={isPickerOpen}
      onClose={() => setIsPickerOpen(false)}
      icao={icao}
      title={kind === 'departure' ? 'Departure FBO' : 'Arrival FBO'}
      selectedFboId={value}
      onSelect={(fboId) => {
        onChange(fboId)
        if (fboId) {
          const picked = fbos?.find((f) => f.id === fboId)
          track(EVENTS.FBO_SELECTED, { icao, kind, source: picked?.source, auto: false })
        }
      }}
    />
  )

  // Selected FBO → picker row (tap to change, swipe left to detach) + contact
  // links indented under it, matching the Figma FBO/Handler block.
  if (selected) {
    return (
      <>
        <SwipeableRow
          isOpen={isRowOpen}
          onOpenChange={setIsRowOpen}
          onDelete={() => {
            setIsRowOpen(false)
            onChange(null)
          }}
          variant="icon"
          actionLabel="Remove"
          actionClassName="bg-content-primary-subtle"
          actionIcon={<CircleMinus />}
          disabled={readOnly}
        >
          <div>
            <PrntrPickerRow
              label="FBO / Handler"
              iconLeft={<Building2 />}
              value={selected.name}
              onClick={openPicker}
              readOnly={readOnly}
              hideChevron
            />
            {(selected.phone || selected.email) && (
              <div className="flex flex-col gap-2 py-2 pl-6 text-sm">
                {selected.phone && (
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-content-primary-subtle">Phone</span>
                    <PhoneLink phone={selected.phone} />
                  </div>
                )}
                {selected.email && (
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-content-primary-subtle">Email</span>
                    <EmailLink email={selected.email} />
                  </div>
                )}
              </div>
            )}
          </div>
        </SwipeableRow>
        {drawers}
      </>
    )
  }

  // Nothing selected. Read-only viewers see nothing rather than affordances.
  if (readOnly) return null

  return (
    <>
      <PrntrPickerRow
        label="FBO / Handler"
        iconLeft={<Building2 />}
        value={null}
        placeholder="Add"
        onClick={openPicker}
        hideChevron
      />
      {drawers}
    </>
  )
}
